/**
 * The Product Usage role rule: who counts as a champion, key builder, or builder.
 *
 * Pure and dependency-free so both the account tab read path (`services/product-usage/read.ts`)
 * and the Flow contact-state loader apply the same definition. The threshold and the won-stage
 * label live here for the same reason: one place to change, one place to test.
 */
import type { ContactRole } from './wire';

/**
 * Share of the account's ATTRIBUTED consumption that makes someone a KEY builder.
 *
 * 10%, from the reviewed definition. Applied at read time rather than stored so changing it
 * does not require re-syncing the warehouse: the share is the durable fact, the threshold a
 * judgement.
 *
 * Normalising within the attribution model rather than against the pacing model's consumed
 * figure did not change who clears the bar on any account checked (Okta 74.1%→66.5% and
 * 22.6%→20.3%, Anthropic 70.2%→75.7%, OpenAI 89.3%→87.1%), which is the evidence that this
 * preserves the definition rather than redefining it.
 */
export const KEY_BUILDER_SHARE = 0.1;

/**
 * The stage label that makes an opportunity won.
 *
 * Verified against prod: `Closed Won` is the only won stage (4,303 opportunities, 2,846 of
 * them carrying a `champion_id`). Compared case-insensitively so a re-cased label does not
 * silently empty the champion set.
 */
export const WON_STAGE = 'closed won';

export function isWonStage(stage: string | null | undefined): boolean {
  return (stage ?? '').trim().toLowerCase() === WON_STAGE;
}

/** Most significant first, the order the read path emits and a UI renders without re-sorting. */
export const ROLE_ORDER: readonly ContactRole[] = ['champion', 'key_builder', 'builder'];

/** Rep-facing label per role: the chip text and the filter picker's field names. */
export const ROLE_LABEL: Record<ContactRole, string> = {
  champion: 'Champion',
  key_builder: 'Key builder',
  builder: 'Builder',
};

/**
 * The three per-role SQL booleans the contacts list SELECTs (`is_champion`,
 * `is_key_builder`, `is_builder`; src/api/flow/contact-field-map.ts), keyed
 * by their catalog field key so the wire mapper can read them off the raw row
 * by name. Nullable because a contact with no account row never evaluates the
 * EXISTS at all.
 */
export interface UsageRoleFlags {
  is_champion: boolean | null | undefined;
  is_key_builder: boolean | null | undefined;
  is_builder: boolean | null | undefined;
}

/** Which flag carries each role, in ROLE_ORDER so the fold needs no re-sort. */
const FLAG_FOR_ROLE: Record<ContactRole, keyof UsageRoleFlags> = {
  champion: 'is_champion',
  key_builder: 'is_key_builder',
  builder: 'is_builder',
};

/**
 * Fold the three SQL booleans back into one ordered role list.
 *
 * The list query cannot emit a role ARRAY without evaluating each EXISTS twice
 * (once for the filter column, once for the array), so it emits the flags and
 * this fold happens once per row on the wire. A null flag is "not that role":
 * the only way it is null is a contact with no account row, and such a contact
 * cannot hold any role.
 */
export function rolesFromFlags(flags: UsageRoleFlags): ContactRole[] {
  return ROLE_ORDER.filter((role) => flags[FLAG_FOR_ROLE[role]] === true);
}

/**
 * Dedupe and order a set of roles by significance.
 *
 * For callers that union roles across several workspaces on one account, where the same
 * person can clear the bar in more than one and the roles arrive in arbitrary order.
 */
export function sortRoles(roles: Iterable<ContactRole>): ContactRole[] {
  const present = new Set(roles);
  return ROLE_ORDER.filter((role) => present.has(role));
}

/**
 * Every role that applies, most significant first.
 *
 * Deliberately additive: champion and key builder routinely coexist, and collapsing to one
 * label would drop whichever the caller happened to check second. A key builder is also a
 * builder: the narrower claim does not replace the broader one.
 *
 * Champion status is decided by the caller, since it depends on a Salesforce opportunity
 * lookup this pure module does not perform.
 */
export function rolesFor(
  row: {
    creditShareOfAttributed: number | null;
    tablesCreated: number | null;
    fieldsCreated: number | null;
  },
  isChampion: boolean,
): ContactRole[] {
  const roles: ContactRole[] = [];
  if (isChampion) roles.push('champion');
  if ((row.creditShareOfAttributed ?? 0) >= KEY_BUILDER_SHARE) roles.push('key_builder');
  // The reviewed definition of a builder: they created something, rather than merely
  // consuming credits someone else's table spent.
  if ((row.tablesCreated ?? 0) > 0 || (row.fieldsCreated ?? 0) > 0) roles.push('builder');
  return roles;
}
